import { useState, useRef, useEffect } from 'react';
import { gql, useLazyQuery, useQuery, useMutation } from '@apollo/client';
import { Box, TextField, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar, showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import { useSelector } from 'react-redux';
import { selectUser } from '../../features/auth/authSlice';
import { IProjectAccess } from '../../types/user';
import { IUser } from '../../types/user';
import { IRegistrant } from '../../types/registrant';
import { selectProject } from '../../features/project/projectSlice';
import { convertAllDates } from '../../utils/Functions';
import { IDeal } from '../../types/CreateDealForm';

const Texts = (props: ChildProps) => {
  const user = useSelector(selectUser);
  const project = useSelector(selectProject);
  const storeDispatch = useAppDispatch();
  const { email, phone, type, deal } = props;
  const [registrant, setRegistrant] = useState<IRegistrant | null>(null);

  const [text, setText] = useState<string>('');
  const divRef: any = useRef();

  const [textMessages, setTextMessages] = useState<IText | null>(null);

  useQuery(GETREGISTRANT, {
    variables: { filter: { project: project._id, email: email } },
    onCompleted: (data) => {
      setRegistrant(data.registrantOne);
    },
    onError: (err) => console.log(err),
  });

  const [addComment] = useMutation(ADDCOMMENT, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollIntoView({ block: 'end', inline: 'end' });
    }
  }, [divRef, textMessages]);

  useEffect(() => {
    if (registrant) {
      getTextMessages({ variables: { filter: { OR: [{ registrant: registrant?._id }] } } });
    }
  }, [registrant]);

  const [getTextMessages] = useLazyQuery(TEXTMESSAGES, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      let textMessageObject: IText | null = null;
      if (data.textMessageMany.length) {
        textMessageObject = {
          messages: data.textMessageMany.map((text: any) => {
            return {
              content: text.text,
              direction: text.type,
              created_at: text.createdAt,
              registrant: registrant,
              user: text.user,
            };
          }),
          lastMessage: data.textMessageMany[data.textMessageMany.length - 1].createdAt,
          name: project.name,
          phoneNumber: registrant?.primaryPhone!,
        };
      }
      setTextMessages(textMessageObject);
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar('Error getting appointments'));
    },
  });

  const [textRegistrant, { loading }] = useLazyQuery(TEXTREGISTRANT, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setText('');
      setTextMessages({
        messages: textMessages?.messages.length
          ? [
              ...textMessages.messages,
              {
                content: data.textRegistrant.messages[data.textRegistrant.messages.length - 1].content,
                direction: data.textRegistrant.messages[data.textRegistrant.messages.length - 1].direction,
                created_at: data.textRegistrant.messages[data.textRegistrant.messages.length - 1].created_at,
                registrant: registrant,
                user: user,
              },
            ]
          : [
              {
                content: data.textRegistrant.messages[data.textRegistrant.messages.length - 1].content,
                direction: data.textRegistrant.messages[data.textRegistrant.messages.length - 1].direction,
                created_at: data.textRegistrant.messages[data.textRegistrant.messages.length - 1].created_at,
                registrant: registrant,
                user: user,
              },
            ],
        lastMessage: data.textRegistrant.messages[data.textRegistrant.messages.length - 1].created_at,
        name: data.textRegistrant.name,
        phoneNumber: data.textRegistrant.phoneNumber,
      });
      let newObject = {
        project: project._id,
        unit: deal.unit?._id,
        deal: deal._id,
        user: user._id,
        comment: `SMS has been sent to ${data.textRegistrant.phoneNumber}`,
        type: 'general',
        pinned: false,
        deleted: false,
      };
      addComment({ variables: { record: newObject } });
      storeDispatch(showSuccessSnackbar('Text Message Sent!'));
    },
    onError: (e) => {
      console.log(e);
    },
  });

  const submitText = (e: any, data: IText) => {
    let contactPhone = registrant?.primaryPhone;
    if (phone) {
      contactPhone = phone;
    }
    if (!data && (e.key === 'Enter' || e.type === 'click') && !e.shiftKey) {
      if (!contactPhone) return storeDispatch(showErrorSnackbar('This user does not have a phone number'));
      contactPhone = contactPhone.replace(/\D/g, '');
      if (contactPhone?.length < 10 && contactPhone?.length > 11) {
        return storeDispatch(showErrorSnackbar('Only US/Canadian Phone Numbers are allowed'));
      }

      let projectId = user?.projectAccess.find((projectAccess: IProjectAccess) => projectAccess.project.name === project.name);
      if (!text) return storeDispatch(showErrorSnackbar('No Message Found'));

      textRegistrant({
        variables: { project: projectId?.project._id, customerPhone: parseInt(contactPhone, 10), message: text, type: type },
      });
      e.preventDefault();
    } else if ((e.key === 'Enter' || e.type === 'click') && !e.shiftKey) {
      if (!contactPhone) return storeDispatch(showErrorSnackbar('This registrant does not have a phone number'));
      contactPhone = contactPhone.replace(/\D/g, '');
      if (contactPhone.length < 10 && contactPhone.length > 11) {
        return storeDispatch(showErrorSnackbar('Only US/Canadian Phone Numbers are allowed'));
      }

      let projectId = user?.projectAccess.find((project: IProjectAccess) => project.project.name === data.name);
      if (!text) return storeDispatch(showErrorSnackbar('No Message Found'));

      textRegistrant({
        variables: { project: projectId?.project._id, customerPhone: parseInt(contactPhone, 10), message: text, type: type },
      });
      e.preventDefault();
    }
  };

  return (
    <>
      <Box sx={{ backgroundColor: '#fff', p: 1, border: '1px solid #000' }}>
        <Typography variant="h3">{registrant?.fullName}</Typography>
      </Box>
      <Box sx={{ p: 2, border: '1px solid #000', backgroundColor: '#c8ddf1', maxHeight: '500px', overflowY: 'auto', height: '100%' }}>
        {textMessages ? (
          <ChatContainer>
            {textMessages.messages.map((messageData: any, index: number) => {
              if (messageData.direction === 'outgoing') {
                return (
                  <MessageSent key={index}>
                    <Typography sx={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: messageData.content }}></Typography>
                    <Box sx={{ mt: 1, fontSize: '9px', textAlign: 'right' }}>
                      {messageData.user ? messageData.user.fullName : 'Unknown'} - {convertAllDates(messageData.created_at, 'Pp')}
                    </Box>
                  </MessageSent>
                );
              } else {
                return (
                  <MessageReceived key={index}>
                    <Typography sx={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: messageData.content }}></Typography>
                    <Box
                      sx={{ mt: 1, fontSize: '9px', cursor: 'pointer', fontWeight: messageData.registrant ? 600 : 500 }}
                      onClick={() =>
                        messageData.registrant
                          ? window.open(
                              `/dashboard/project/${messageData.registrant?.project._id}/registrant/${messageData.registrant._id}`,
                              '_blank',
                              'rel=noopener noreferrer'
                            )
                          : null
                      }
                    >
                      <Box>
                        {registrant?.fullName} - {convertAllDates(messageData.created_at, 'Pp')}
                      </Box>
                    </Box>
                  </MessageReceived>
                );
              }
            })}
            <Box sx={{ mt: 'auto', position: 'relative' }}>
              <TextField
                aria-label="minimum height"
                multiline
                minRows={4}
                onKeyDown={(e) => submitText(e, textMessages)}
                onChange={(e) => setText(e.target.value)}
                value={text}
                placeholder={'Reply'}
                sx={{
                  width: '100%',
                  mt: 2,
                  mb: textMessages.messages.length > 4 ? 2 : 0,
                  backgroundColor: '#fff',
                  opacity: loading ? 0.5 : 1,
                  borderRadius: '8px',
                  '& .MuiInputBase-root': {
                    px: 1,
                    pt: 1,
                    pb: 3,
                  },
                }}
              />
              <IconButton
                onClick={(e) => submitText(e, textMessages)}
                sx={{ color: '#2e453d;', cursor: 'pointer', position: 'absolute', bottom: 15, right: 5 }}
              >
                <SendIcon />
              </IconButton>
            </Box>
            <div id={textMessages.phoneNumber} ref={divRef}></div>
          </ChatContainer>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <em>No conversation has been started.</em>
            <Box sx={{ mt: 2, position: 'relative' }}>
              <TextField
                aria-label="minimum height"
                multiline
                minRows={4}
                onKeyDown={(e) => submitText(e, textMessages!)}
                onChange={(e) => setText(e.target.value)}
                value={text}
                placeholder={`Send a text message`}
                sx={{
                  width: '100%',
                  mb: 2,
                  backgroundColor: '#fff',
                  opacity: loading ? 0.5 : 1,
                  borderRadius: '8px',
                  '& .MuiInputBase-root': {
                    px: 1,
                    pt: 1,
                    pb: 3,
                  },
                }}
              />
              <IconButton
                onClick={(e) => submitText(e, textMessages!)}
                sx={{ color: '#2e453d;', cursor: 'pointer', position: 'absolute', top: 5, right: 5 }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

interface ChildProps {
  phone: string;
  email: string;
  type: string;
  deal: IDeal;
}

export interface IText {
  lastMessage: Date | string;
  messages: ITextMessage[];
  name: string;
  phoneNumber: string;
}

export interface ITextMessage {
  content: string;
  created_at: Date | string;
  direction: string;
  user?: IUser | null;
  messageType?: string;
  registrant?: IRegistrant | null;
  archived?: boolean;
}

export const ChatContainer = styled(Box)`
  --rad: 20px;
  --rad-sm: 3px;
  font: 16px/1.5 sans-serif;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const MessageSent = styled(Box)`
  position: relative;
  max-width: 75%;
  padding: 7px 15px;
  margin-bottom: 2px;
  border-radius: var(--rad) var(--rad-sm) var(--rad-sm) var(--rad);
  background: #2e453d;
  color: #fff;
  /* moves it to the right */
  margin-left: auto;
  border-top-right-radius: var(--rad);
  margin-top: 10px;
`;

export const MessageReceived = styled(Box)`
  position: relative;
  max-width: 75%;
  padding: 7px 15px;
  margin-bottom: 2px;
  border-radius: var(--rad-sm) var(--rad) var(--rad) var(--rad-sm);
  background: #f1f1f1;
  color: #555;
  /* moves it to the left */
  margin-right: auto;
  border-top-left-radius: var(--rad);
  margin-top: 10px;
`;

const GETREGISTRANT = gql`
  query registrantOne($filter: FilterFindOneRegistrantInput) {
    registrantOne(filter: $filter) {
      _id
      email
      firstName
      lastName
      primaryPhone
      postalCode
    }
  }
`;

const TEXTREGISTRANT = gql`
  query textRegistrant($project: MongoID!, $customerPhone: Float!, $message: String!, $type: String) {
    textRegistrant(project: $project, customerPhone: $customerPhone, message: $message, type: $type) {
      lastMessage
      phoneNumber
      messages {
        direction
        content
        created_at
      }
      name
    }
  }
`;

const TEXTMESSAGES = gql`
  query textMessageMany($filter: FilterFindManyTextMessageInput) {
    textMessageMany(filter: $filter, limit: 10000) {
      _id
      type
      text
      project {
        _id
      }
      user {
        fullName
      }
      registrant {
        project {
          _id
        }
        primaryPhone
        fullName
      }
      primaryPhone
      createdAt
    }
  }
`;

const ADDCOMMENT = gql`
  mutation commentCreateOne($record: CreateOneCommentInput!) {
    commentCreateOne(record: $record) {
      record {
        _id
        type
        pinned
        comment
        deal {
          _id
        }
        updatedAt
        createdAt
        user {
          fullName
        }
      }
    }
  }
`;

export default Texts;
